import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Calendar, CheckCircle, Circle } from "lucide-react";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import moment from "moment-timezone";
import { cn } from "@/lib/utils";

interface MultiPeriodSelectorProps {
  availablePeriods: ReportingPeriod[];
  selectedPeriods: ReportingPeriod[];
  onAddPeriod: (period: ReportingPeriod) => void;
  onRemovePeriod: (periodId: string) => void;
  onClearAll: () => void;
  onSelectLastN: (n: number) => void;
  onSelectAll: () => void;
  isPeriodSelected: (periodId: string) => boolean;
  isLoading: boolean;
}

export const MultiPeriodSelector: React.FC<MultiPeriodSelectorProps> = ({
  availablePeriods,
  selectedPeriods,
  onAddPeriod,
  onRemovePeriod,
  onClearAll,
  onSelectLastN,
  onSelectAll,
  isPeriodSelected,
  isLoading,
}) => {
  const handlePeriodToggle = (period: ReportingPeriod) => {
    if (isPeriodSelected(period.reportingPeriodId)) {
      onRemovePeriod(period.reportingPeriodId);
    } else {
      onAddPeriod(period);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Select Reporting Periods
        </CardTitle>
        <CardDescription>
          Choose multiple periods with holdings to analyze currency trends over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Quick Selection Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectLastN(3)}
              disabled={isLoading || availablePeriods.length < 3}
            >
              Last 3 Periods
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectLastN(6)}
              disabled={isLoading || availablePeriods.length < 6}
            >
              Last 6 Periods
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectLastN(12)}
              disabled={isLoading || availablePeriods.length < 12}
            >
              Last 12 Periods
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectAll}
              disabled={isLoading || availablePeriods.length === 0}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAll}
              disabled={isLoading || selectedPeriods.length === 0}
            >
              Clear All
            </Button>
          </div>

          {/* Selected Periods Display */}
          {selectedPeriods.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">
                Selected Periods ({selectedPeriods.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedPeriods
                  .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime())
                  .map((period) => (
                    <Badge
                      key={period.reportingPeriodId}
                      variant="default"
                      className="flex items-center gap-1 pr-1"
                    >
                      <span>{moment(period.period).format("MMM YYYY")}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-primary-foreground/20"
                        onClick={() => onRemovePeriod(period.reportingPeriodId)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
              </div>
            </div>
          )}

          {/* Available Periods Grid */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-muted-foreground">
                Available Periods with Holdings ({availablePeriods.length})
              </h4>
              {availablePeriods.length > 8 && (
                <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                  Scroll to see more periods
                </div>
              )}
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : availablePeriods.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No reporting periods with holdings available
              </div>
            ) : (
              <div className="relative">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto border rounded-md p-2 bg-muted/10">
                  {availablePeriods
                  .sort((a, b) => new Date(b.period).getTime() - new Date(a.period).getTime()) // Most recent first
                  .map((period) => {
                    const isSelected = isPeriodSelected(period.reportingPeriodId);
                    const isCompleted = !period.isOpen;
                    
                    return (
                      <div
                        key={period.reportingPeriodId}
                        className={cn(
                          "flex items-center space-x-2 p-2 rounded-md border cursor-pointer transition-colors",
                          isSelected 
                            ? "bg-primary/10 border-primary/30" 
                            : "bg-background hover:bg-muted/50 border-border"
                        )}
                        onClick={() => handlePeriodToggle(period)}
                      >
                        <div className="flex items-center">
                          {isSelected ? (
                            <CheckCircle className="h-4 w-4 text-primary" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {moment(period.period).format("MMM Do, YYYY")}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <Badge
                              variant={isCompleted ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {isCompleted ? "Closed" : "Open"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Info Message */}
          {selectedPeriods.length > 0 && (
            <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-md">
              💡 Select at least 2 periods to view meaningful currency trends. 
              More periods will provide better insights into long-term patterns.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}; 